/**
 * /dashboard/hr/onboarding/$id — Single onboarding-instance detail
 * (Phase 3c — 2026-06-01).
 *
 * GET /hr/onboarding/{id} 404s a non-authorized caller (HR / the
 * subject / the subject's direct manager are the only readers — no
 * existence oracle), so reaching this view means the caller may read
 * it. Task complete/skip authz + the instance recompute are
 * server-side; OnboardingDetail handles the loading / not-available /
 * error states and reconciles from the server's recomputed response.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/hr/onboarding/[id]/page.tsx +
 * layout.tsx, flattened into one route that uses HrModuleShell. Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 */

import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { HrFeatureGate } from "@/components/hr/hr-feature-gate";
import { HrModuleShell } from "@/components/shared/hr-module-shell";
import { OnboardingDetail } from "@/components/hr/onboarding-detail";
import { useOnboardingInstance } from "@/hooks/use-hr";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hr/onboarding/$id",
)({
  // `?from=inbox` (an Inbox → Tasks row) puts the page on the Inbox.
  validateSearch: z.object({ from: z.enum(["inbox"]).optional().catch(undefined) }),
  component: HrOnboardingDetailRoute,
});

function HrOnboardingDetailRoute() {
  const { id } = Route.useParams();
  const { from } = Route.useSearch();
  const numId = Number(id);
  const { user } = useAuth();
  // Someone else's checklist is titled with their name, so the breadcrumb
  // and the tab title read the record; your own stays "Onboarding".
  const q = useOnboardingInstance(numId);
  const ob = q.data?.onboarding;
  const title =
    ob && ob.user_id !== user?.user_id && ob.employee_name ? ob.employee_name : "Onboarding";

  return (
    <HrModuleShell
      title={title}
      description="Checklist progress and per-task actions."
    >
      <HrFeatureGate>
        {Number.isFinite(numId) && numId > 0 ? (
          <OnboardingDetail instanceId={numId} fromInbox={from === "inbox"} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Invalid onboarding reference.
          </p>
        )}
      </HrFeatureGate>
    </HrModuleShell>
  );
}
